import * as jose from 'jose';
import { useCallback } from 'react';

import { useAuth0 } from '@auth0/auth0-react';

import useGlobalStore from 'hooks/useGlobalStore';

const useAccessToken = () => {
    const { getValue, setValue } = useGlobalStore({
        namespace: 'useAccessToken'
    });

    const { getValue: getCurrentOrganizationValue } = useGlobalStore({
        namespace: 'useCurrentOrganization'
    });

    const { getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();

    const accessToken = getValue('accessToken');
    const decodedToken = getValue('decodedToken');

    const fetchToken = useCallback(
        async (orgId?: string, allowCache = true) => {
            if (isLoading || !isAuthenticated) {
                throw new Error('System not ready for this hook. User information unavailable.');
            }

            const currentOrg = getCurrentOrganizationValue('currentOrganization');
            const effectiveOrgId = orgId ?? currentOrg?.auth0Id;

            const updatedAccessToken = await getAccessTokenSilently({
                authorizationParams: {
                    audience: import.meta.env.VITE_AUTH0_AUDIENCE,
                    ...(effectiveOrgId && { organization: effectiveOrgId }),
                    scope: 'read:current_user'
                },
                cacheMode: allowCache ? 'on' : 'off'
            });

            const existingAccessToken = getValue('accessToken');

            if (!existingAccessToken || existingAccessToken !== updatedAccessToken) {
                setValue({ accessToken: updatedAccessToken });
                const decoded = jose.decodeJwt(updatedAccessToken);

                setValue({ decodedToken: decoded });
            }

            return updatedAccessToken;
        },
        [
            getAccessTokenSilently,
            isAuthenticated,
            isLoading,
            getCurrentOrganizationValue,
            getValue,
            setValue
        ]
    );

    return {
        accessToken,
        decodedToken,
        fetchToken
    };
};

export default useAccessToken;
